﻿import {hasFlag, translation, UserError} from "@2d-front-monorepo/core";
import {
	useInitializeMvvmContext,
	LinkButton,
	MvvmButton,
	MvvmDataProvider,
	MvvmTextInput,
	trim
} from "@2d-front-monorepo/design-library";
import {PermitsMvvmErrorCollector} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import React, {JSX, useCallback, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router";
import {HttpContext} from "../../contexts/HttpContext";
import {getDefaultLandingPage} from "../../functions";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {ReloadEntities, reloadPermits} from "../../hooks/reloadPermits";
import useEmailRequiredPolicy from "../../policies/emailRequiredPolicy";
import usePasswordRequiredPolicy from "../../policies/passwordRequiredPolicy";
import {PublicRoutes} from "../../routes";

type SignInProps = {
	email: string;
	password: string;
}

export default function SignIn(): JSX.Element {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const global = getGlobalContext();
	const [email, setEmail] = useState(function () {
		return searchParams.get("email");
	});

	const {context} = useInitializeMvvmContext({
		initialState: () => {
			return {
				email: email ?? "",
				password: ""
			};
		}
	});

	const onSubmit = useCallback(async (data: SignInProps) => {
		const httpContext = HttpContext.getClient();

		await httpContext.authentication.signIn({
			email: data.email,
			authentication: {
				$type: "password",
				password: data.password
			},
			installationId: global.installation.id,
			languageId: global.languageId
		});

		const loaded = await reloadPermits(ReloadEntities.All);
		if (!hasFlag(loaded, ReloadEntities.Holder)) {
			// Sign-in succeeded but the account load failed (reloadPermits logs and swallows):
			// navigating now would bounce off PrivateRoute back to the welcome page with no error.
			throw new UserError({text: PermitTranslations.form_error__title}, {text: PermitTranslations.sign_in__load_failed});
		}
		navigate(getDefaultLandingPage(), {replace: true});
	}, []);

	return (
		<section className="sign-in full-layout full-layout--centered">
			<MvvmDataProvider<SignInProps> context={context} onSubmit={onSubmit}>
				<h1>{translation(PermitTranslations.sign_in)}</h1>
				<div className="layout vertical gap">
					<PermitsMvvmErrorCollector/>

					<MvvmTextInput type="email"
					               context={context}
					               property="email"
					               autoComplete="email webauthn"
					               autoCapitalize="none"
					               label={translation(PermitTranslations.email)}
					               onChange={e => setEmail(e ?? "")}
					               validation={useEmailRequiredPolicy()}/>

					<MvvmTextInput type="password"
					               context={context}
					               property="password"
					               autoComplete="current-password webauthn"
					               label={translation(PermitTranslations.password)}
					               validation={usePasswordRequiredPolicy(false)}/>

					<MvvmButton kind="primary">
						{translation(PermitTranslations.sign_in)}
					</MvvmButton>

					<LinkButton to={PublicRoutes.home}>
						{translation(PermitTranslations.cancel)}
					</LinkButton>

					<Link to={`${PublicRoutes.forgotPassword}?email=${email == null ? "" : encodeURIComponent(trim(email))}`}
					      className="form__link">
						{translation(PermitTranslations.sign_in_page__forgot_password)}
					</Link>
				</div>
			</MvvmDataProvider>
		</section>
	)
}
